import React, { useContext } from 'react';
import { TenantContext } from '../contexts/TenantContext';

/**
 * Tenant-aware brand logo. Renders the NoiRock logo by default and swaps to
 * the TCN logo when the current tenant slug is "tcn". Pass `variant="noirock"`
 * or `variant="tcn"` to force a specific logo regardless of context.
 *
 * Both PNGs (256×256) ship with their own black background, so the component
 * uses CSS border-radius to soften the corners on top of any surface.
 *
 * <NoiRockLogo size={36} />                  auto, icon only
 * <NoiRockLogo size={36} withText />         auto, icon + wordmark
 * <NoiRockLogo size={28} variant="noirock"/> force NoiRock branding
 */
const NoiRockLogo = ({ size = 36, withText = false, className = '', variant = 'auto' }) => {
  const tenantCtx = useContext(TenantContext);
  // effectiveTenantSlug falls back to the hostname-pinned slug (tcn.noirock.app)
  // so the logo is correct before login on a dedicated subdomain.
  const tenantSlug = tenantCtx?.effectiveTenantSlug ?? tenantCtx?.tenantSlug ?? null;

  const resolved = variant === 'auto'
    ? (tenantSlug === 'tcn' ? 'tcn' : 'noirock')
    : variant;

  const isTcn = resolved === 'tcn';
  const src = isTcn ? '/logo-tcn.png' : '/logo-noirock.png';
  const alt = isTcn ? 'TCN' : 'NoiRock';
  const text = isTcn ? 'TCN' : 'NoiRock';
  const radius = Math.max(6, Math.round(size * 0.22));

  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <img
        src={src}
        alt={alt}
        width={size}
        height={size}
        style={{ width: size, height: size, borderRadius: radius, display: 'block' }}
        decoding="async"
        loading="eager"
      />
      {withText && (
        <span
          className="font-heading font-bold tracking-tight text-foreground"
          style={{ fontSize: Math.round(size * 0.55), letterSpacing: '0.04em' }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default NoiRockLogo;
